import { useBoundStore } from "@/app/store";
import { useEffect, useRef } from "react";
import { useShallow } from "zustand/shallow";

interface UseKeyboardAwareBottomProps {
  disabled?: boolean;
  offset?: number;
}

export function useSetCssViewportVars({ disabled = false, offset = 0 }: UseKeyboardAwareBottomProps = {}) {
  const devicePlatform = useBoundStore(useShallow((state) => state.device.platform));
  const lastHeightRef = useRef(window.visualViewport?.height ?? window.innerHeight);

  useEffect(() => {
    const viewport = window.visualViewport;

    if (!viewport || disabled) return;

    const updatePosition = () => {
      // On Android, the visual viewport is equitable to window.innerHeight changes, making it impossible to detect keyboard state.
      // Thus, we went with checking the old vs new window.innerHeight values.
      const currentHeight = viewport.height;
      const diff = lastHeightRef.current - currentHeight;

      // If diff > 50px, keyboard is likely open; otherwise closed.
      const isKeyboardOpen = diff > 50;
      const keyboardHeight = Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop);
      const newBottom = isKeyboardOpen ? keyboardHeight + offset : offset;
      
      document.documentElement.style.setProperty("--viewport-bottom", `${newBottom}px`);
      document.documentElement.style.setProperty("--keyboard-height", `${keyboardHeight}px`);
      document.documentElement.style.setProperty("--viewport-height", `${viewport.height}px`);
      // This is used to remove padding/margin at the bottom when keyboard is open on iOS
      document.documentElement.style.setProperty(
        "--inset-bottom",
        isKeyboardOpen ? "0px" : "var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px))"
      );
    };

    viewport.addEventListener("resize", updatePosition);

    updatePosition();

    return () => {
      viewport.removeEventListener("resize", updatePosition);
    };
  }, [disabled, devicePlatform, offset]);
}
